import './ExchangeCredentialsModal.scss'
import React from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ReactComponent as ZocksLogo } from '../../../../../assets/images/ZocksIcon-white.svg'
import { useCreateUserIntegrationMutation } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'

export type ExchangeCredentialsFormValues = {
  serverUrl: string
  username: string
  password: string
}

export interface ExchangeCredentialsModalProps {
  show: boolean
  onSave: () => void
  onCancel: () => void
}

export const ExchangeCredentialsModal: React.FC<ExchangeCredentialsModalProps> = ({ show, onSave, onCancel }) => {
  const defaultValues = {
    serverUrl: '',
    username: '',
    password: '',
  }

  const form = useForm<ExchangeCredentialsFormValues>({ defaultValues })
  const [createUserIntegration, { data: userIntegrationData, isLoading: isCreatingIntegration, isSuccess }] = useCreateUserIntegrationMutation()
  const accountId = useAppSelector(selectAccountId)

  const { handleSubmit } = form

  const handleSave = async (data: ExchangeCredentialsFormValues) => {
    try {
      await createUserIntegration({
        accountId,
        integrationName: IntegrationServiceId.EXCHANGE_CALENDAR,
        payload: {
          serverUrl: data.serverUrl,
          username: data.username,
          password: data.password,
        },
      }).unwrap()
      onSave()
    } catch (e) {
      console.error(e)
    }
  }

  const handleOnHide = () => {
    form.reset()
    onCancel()
  }

  const body = (
    <Container>
      <Row className='zocks-pb-2'>
        <Stack direction='horizontal' className='d-flex justify-content-center align-items-center' gap={3}>
          <div className='zocks-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <ZocksLogo width='3rem' height='3rem' />
          </div>
          <i className='bi bi-arrow-left-right'></i>
          <div className='exchange-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <IntegrationIcon serviceId={IntegrationServiceId.EXCHANGE_CALENDAR} fill='white' width='3rem' height='3rem' />
          </div>
        </Stack>
      </Row>
      <Row className='pb-3'>
        <Col>
          <h3 className='d-flex justify-content-center align-items-center'>Connect Exchange (On-Premise)</h3>
        </Col>
      </Row>
      <Row className='pb-3'>
        <Col>
          <p className='d-flex justify-content-center align-items-center'>Please enter your Exchange credentials</p>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <ExchangeCredentialsForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' onClick={handleSubmit(handleSave)} loading={isCreatingIntegration}>
        Save
      </Button>
    </div>
  )

  return <Modal show={show} onHide={handleOnHide} header={<></>} body={body} footer={footer} centered={true} />
}

const ExchangeCredentialsForm: React.FC = () => {
  const { control } = useFormContext<ExchangeCredentialsFormValues>()

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col>
          <Controller
            name='serverUrl'
            control={control}
            rules={{
              required: 'Exchange server url is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>
                  Exchange Login URL
                  <Tooltip
                    className='zocks-text-gray-500'
                    placement='right'
                    title='Please enter the server address make sure to include https:// and without the /owa suffix. Example: https://mail.company.com'
                  >
                    <i className='bi bi-info-circle' />
                  </Tooltip>
                </Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter exchange login url' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='username'
            control={control}
            rules={{
              required: 'Email address is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Email address</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter email address' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='password'
            control={control}
            rules={{
              required: 'Password is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Password</Form.Label>
                <Form.Control id={field.name} type='password' placeholder='Enter password…' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
